import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * ScaleBox keeps a fixed aspect ratio and scales a fixed-size "design canvas"
 * proportionally to the available width. Everything inside stays in place.
 *
 * - baseWidth / baseHeight: your original px design size
 * - ratioClass: Tailwind aspect class that matches baseWidth:baseHeight
 */
const ScaleBox = ({
  baseWidth,
  baseHeight,
  ratioClass,
  className = '',
  children,
}) => {
  const outerRef = useRef(null);
  const rafRef = useRef(0);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    const computeScale = () => {
      const w = el.clientWidth || baseWidth;

      // Width-based scaling is the most stable when ratioClass enforces aspect ratio
      const raw = w / baseWidth;

      // Clamp + round to reduce subpixel jitter
      const clamped = Math.max(0.01, raw);
      const rounded = Math.round(clamped * 1000) / 1000; // 3 decimals is plenty

      // Deadband: ignore micro changes that cause visible "settle" shrink
      setScale((prev) => (Math.abs(prev - rounded) < 0.002 ? prev : rounded));
    };

    const onResize = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(computeScale);
    };

    const ro = new ResizeObserver(onResize);
    ro.observe(el);

    // Initial compute (before paint)
    computeScale();

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [baseWidth]);

  return (
    <div
      ref={outerRef}
      className={`relative w-full ${ratioClass} ${className}`}
      style={{
        // Helps isolate layout/paint changes inside the box
        contain: 'layout paint',
      }}
    >
      <div
        className="absolute top-0 left-0 origin-top-left"
        style={{
          width: baseWidth,
          height: baseHeight,
          transform: `scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default ScaleBox;
